import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { Button, FormField, Modal, Skeleton, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { openPdf } from '@/lib/pdf';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type AttachementSTDTO,
  type ContratSTDTO,
  createAttachementST,
  deleteAttachementST,
  listAttachementsST,
  listContratsST,
  validerAttachementST,
} from '@/services/finance';
import { listUtilisateurs } from '@/services/parametres';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const STATUT_UI: Record<string, { label: string; status: 'pending' | 'info' | 'done' }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  VALIDE: { label: 'Validé', status: 'info' },
  PAYE: { label: 'Payé', status: 'done' },
};
const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => Number(s) || 0;
type LigneForm = { designation: string; unite: string; quantite_periode: string; quantite_cumul: string; prix_unitaire: string };
const ligneVide = (): LigneForm => ({ designation: '', unite: '', quantite_periode: '', quantite_cumul: '', prix_unitaire: '' });

export function AttachementsSTTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['attachements-st'], queryFn: () => listAttachementsST() });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['attachements-st'] });
    queryClient.invalidateQueries({ queryKey: ['contrats-st'] });
  };

  const validerMut = useMutation({
    mutationFn: (id: string) => validerAttachementST(id),
    onSuccess: () => { invalidate(); toast.success('Attachement validé. Facture fournisseur générée (net à payer).'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteAttachementST(id),
    onSuccess: () => { invalidate(); toast.success('Attachement supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const items = data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-content-secondary">Constat contradictoire des travaux réalisés → base de paiement du sous-traitant (net après retenue de garantie).</p>
        <Button onClick={() => setOpen(true)}><Plus /> Nouvel attachement</Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-content-muted">Aucun attachement. Créez-en un à partir d'un contrat de sous-traitance.</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => <AttachementRow key={a.id} a={a} onValider={() => validerMut.mutate(a.id)} onDelete={() => deleteMut.mutate(a.id)} validating={validerMut.isPending} />)}
        </div>
      )}

      {open && <AttachementModal onClose={() => setOpen(false)} onSaved={() => { invalidate(); setOpen(false); }} />}
    </div>
  );
}

function AttachementRow({ a, onValider, onDelete, validating }: { a: AttachementSTDTO; onValider: () => void; onDelete: () => void; validating: boolean }) {
  const ui = STATUT_UI[a.statut] ?? STATUT_UI.BROUILLON;
  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-content-primary">{a.reference ?? 'Brouillon'}</span>
            <StatusBadge status={ui.status} label={ui.label} />
            {a.facture_reference && <span className="rounded bg-surface-hover px-2 py-0.5 text-xs text-content-secondary">Facture {a.facture_reference}</span>}
          </div>
          <p className="mt-1 text-sm text-content-secondary">{a.sous_traitant_nom ?? '—'} · {a.chantier_label ?? '—'}</p>
          <p className="text-xs text-content-muted">
            {a.periode_du || a.periode_au ? `Période ${a.periode_du ?? '…'} → ${a.periode_au ?? '…'}` : `Date ${a.date_attachement ?? '—'}`}
            {a.contrat_reference ? ` · Contrat ${a.contrat_reference}` : ''}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-content-muted">Brut <span className="font-medium text-content-secondary">{formatDZD(a.montant_brut)}</span> − RG {Number(a.retenue_garantie_pct)}% <span className="font-medium text-content-secondary">{formatDZD(a.montant_rg)}</span></div>
          <div className="text-lg font-bold tabular-nums text-content-primary">Net {formatDZD(a.montant_net)}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => openPdf(`/sous-traitants/attachements/${a.id}/pdf`)}><FileText className="size-4" /> PDF</Button>
        {a.statut === 'BROUILLON' && <Button variant="secondary" size="sm" onClick={onValider} loading={validating}><ShieldCheck className="size-4" /> Valider</Button>}
        {a.statut === 'BROUILLON' && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={onDelete} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
      </div>
    </div>
  );
}

function AttachementModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: contratsPage } = useQuery({ queryKey: ['contrats-st', { showArchived: false }], queryFn: () => listContratsST({ archives: false }) });
  const { data: users } = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });
  const contrats = useMemo(() => contratsPage?.items ?? [], [contratsPage]);

  const [contratId, setContratId] = useState('');
  const [date, setDate] = useState(today());
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [objet, setObjet] = useState('');
  const [etabliPar, setEtabliPar] = useState('');
  const [rg, setRg] = useState('');
  const [observations, setObservations] = useState('');
  const [lignes, setLignes] = useState<LigneForm[]>([ligneVide()]);

  const contrat: ContratSTDTO | undefined = useMemo(() => contrats.find((c) => c.id === contratId), [contrats, contratId]);

  // Cumul déjà attaché (validés/payés) sur le contrat sélectionné → plafond.
  const { data: attDuContrat } = useQuery({
    queryKey: ['attachements-st', { contrat_id: contratId }],
    queryFn: () => listAttachementsST({ contrat_id: contratId }),
    enabled: !!contratId,
  });
  const cumulAutres = (attDuContrat ?? []).filter((a) => a.statut !== 'BROUILLON').reduce((s, a) => s + Number(a.montant_brut), 0);

  const onPickContrat = (id: string) => {
    setContratId(id);
    const c = contrats.find((x) => x.id === id);
    if (c) setRg(String(Number(c.retenue_garantie_pct)));
  };
  const setLigne = (i: number, patch: Partial<LigneForm>) => setLignes((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLigne = () => setLignes((ls) => [...ls, ligneVide()]);
  const removeLigne = (i: number) => setLignes((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const ligneMontant = (l: LigneForm) => num(l.quantite_periode) * num(l.prix_unitaire);
  const brut = lignes.reduce((s, l) => s + ligneMontant(l), 0);
  const rgPct = num(rg);
  const rgMontant = (brut * rgPct) / 100;
  const net = brut - rgMontant;
  const contratMontant = contrat ? Number(contrat.montant_contrat) : 0;
  const reste = contratMontant - cumulAutres;
  const depasse = contratMontant > 0 && cumulAutres + brut > contratMontant;

  const queryClient = useQueryClient();
  const createMut = useMutation({
    mutationFn: () => createAttachementST({
      sous_traitant_id: contrat!.sous_traitant_id,
      chantier_id: (contrat!.chantier_id || '') as string,
      contrat_id: contratId,
      date_attachement: date || null,
      periode_du: du || null,
      periode_au: au || null,
      objet: objet || null,
      etabli_par_id: etabliPar || null,
      retenue_garantie_pct: rgPct,
      observations: observations || null,
      lignes: lignes.filter((l) => l.designation.trim()).map((l, i) => ({
        designation: l.designation, unite: l.unite || null,
        quantite_periode: num(l.quantite_periode), quantite_cumul: num(l.quantite_cumul),
        prix_unitaire: num(l.prix_unitaire), ordre: i,
      })),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['attachements-st'] }); toast.success('Attachement enregistré (brouillon).'); onSaved(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const peutEnregistrer = !!contrat && (contrat.chantier_id || '') !== '' && lignes.some((l) => l.designation.trim() && ligneMontant(l) > 0);
  const inputCls = 'h-8 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm';

  return (
    <Modal open onOpenChange={(v) => !v && onClose()} title="Nouvel attachement de sous-traitant" size="xl"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!peutEnregistrer}>Enregistrer (brouillon)</Button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Contrat de sous-traitance" required className="sm:col-span-2">
            <select className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm" value={contratId} onChange={(e) => onPickContrat(e.target.value)}>
              <option value="">— Sélectionner un contrat —</option>
              {contrats.map((c) => <option key={c.id} value={c.id}>{c.reference} · {c.objet} ({formatDZD(c.montant_contrat)})</option>)}
            </select>
          </FormField>
          <FormField label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></FormField>
          <FormField label="Établi par (chef de projet)">
            <select className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm" value={etabliPar} onChange={(e) => setEtabliPar(e.target.value)}>
              <option value="">— Moi —</option>
              {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
            </select>
          </FormField>
          <FormField label="Période du"><Input type="date" value={du} onChange={(e) => setDu(e.target.value)} /></FormField>
          <FormField label="Période au"><Input type="date" value={au} onChange={(e) => setAu(e.target.value)} /></FormField>
          <FormField label="Objet" className="sm:col-span-2"><Input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="ex : Terrassement — situation n°2" /></FormField>
        </div>

        {/* Panneau plafond contractuel */}
        {contrat && (
          <div className={`rounded-lg border p-3 text-sm ${depasse ? 'border-status-stuck/40 bg-status-stuck/5' : 'border-border bg-surface-secondary'}`}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div><div className="text-xs text-content-muted">Montant contrat</div><div className="font-semibold tabular-nums">{formatDZD(contratMontant)}</div></div>
              <div><div className="text-xs text-content-muted">Déjà attaché</div><div className="font-semibold tabular-nums">{formatDZD(cumulAutres)}</div></div>
              <div><div className="text-xs text-content-muted">Reste disponible</div><div className="font-semibold tabular-nums">{formatDZD(reste)}</div></div>
              <div><div className="text-xs text-content-muted">Cet attachement (brut)</div><div className="font-semibold tabular-nums">{formatDZD(brut)}</div></div>
            </div>
            {depasse && (
              <div className="mt-2 flex items-center gap-2 font-medium text-status-stuck">
                <AlertTriangle className="size-4" /> Plafond contractuel dépassé de {formatDZD(cumulAutres + brut - contratMontant)} — la validation sera bloquée. Réduisez l'attachement ou ajoutez un avenant.
              </div>
            )}
          </div>
        )}

        {/* Lignes au métré */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="label-caps">Lignes au métré</label>
            <Button variant="ghost" size="sm" onClick={addLigne}><Plus className="size-4" /> Ligne</Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-secondary text-left text-xs text-content-muted">
                  <th className="px-2 py-2">Désignation</th>
                  <th className="px-2 py-2 w-16">Unité</th>
                  <th className="px-2 py-2 w-24 text-right">Qté période</th>
                  <th className="px-2 py-2 w-24 text-right">Qté cumulée</th>
                  <th className="px-2 py-2 w-28 text-right">P.U.</th>
                  <th className="px-2 py-2 w-32 text-right">Montant</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {lignes.map((l, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-2 py-1"><input className={inputCls} value={l.designation} onChange={(e) => setLigne(i, { designation: e.target.value })} placeholder="ex : Déblai en terrain meuble" /></td>
                    <td className="px-2 py-1"><input className={inputCls} value={l.unite} onChange={(e) => setLigne(i, { unite: e.target.value })} placeholder="m³" /></td>
                    <td className="px-2 py-1"><input className={`${inputCls} text-right`} type="number" step="any" value={l.quantite_periode} onChange={(e) => setLigne(i, { quantite_periode: e.target.value })} /></td>
                    <td className="px-2 py-1"><input className={`${inputCls} text-right`} type="number" step="any" value={l.quantite_cumul} onChange={(e) => setLigne(i, { quantite_cumul: e.target.value })} /></td>
                    <td className="px-2 py-1"><input className={`${inputCls} text-right`} type="number" step="any" value={l.prix_unitaire} onChange={(e) => setLigne(i, { prix_unitaire: e.target.value })} /></td>
                    <td className="px-2 py-1 text-right tabular-nums text-content-secondary">{formatDZD(ligneMontant(l))}</td>
                    <td className="px-1 py-1 text-center"><button onClick={() => removeLigne(i)} className="text-status-stuck" aria-label="Retirer"><Trash2 className="size-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totaux Brut → RG → Net */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="w-40">
            <FormField label="Retenue de garantie (%)"><Input type="number" step="any" min="0" max="100" value={rg} onChange={(e) => setRg(e.target.value)} /></FormField>
          </div>
          <div className="ml-auto w-64 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-content-muted">Total brut</span><span className="tabular-nums font-medium">{formatDZD(brut)}</span></div>
            <div className="flex justify-between"><span className="text-content-muted">Retenue de garantie ({rgPct || 0} %)</span><span className="tabular-nums text-status-stuck">− {formatDZD(rgMontant)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 text-base font-bold"><span>Net à payer</span><span className="tabular-nums text-content-primary">{formatDZD(net)}</span></div>
          </div>
        </div>
        <FormField label="Observations"><Input value={observations} onChange={(e) => setObservations(e.target.value)} /></FormField>
        <p className="text-xs text-content-muted">À la validation : la référence AST-AAAA-NNNN est attribuée, les cumuls du contrat sont mis à jour et une facture fournisseur est générée sur le <strong>net à payer</strong> (circuit de règlement habituel : payer / solder / partiel).</p>
      </div>
    </Modal>
  );
}
